import React, { useState } from 'react';
import {
  TextField, Button, Switch, FormControlLabel, MenuItem,
  Typography, Box, Paper, Grid, Select, FormControl, Table, TableBody, TableCell, TableHead, TableRow, Alert, Stack
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';

const dimensionsOptions = ['mm', 'inches'];
const fileTypes = ['cad', 'part_image', 'instructions', 'specs', 'general', 'gcode'];
const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'review_requested', label: 'Review Requested' },
  { value: 'certified', label: 'Certified' },
  { value: 'archived', label: 'Archived' },
];

const NewTDPForm = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tdp_title: '',
    version: '1.0.0',
    status: 'draft',
    designed_by: '',
    standards: [''],
    dimensions_units: 'inches',
    specifications: [''],
    cad_program: '',
    parts_list: [],
    attached_files: [],
    asset_refs: [''],
    build_history: [''],
    telemetry_refs: [''],
    certifications: [''],
    related_exchange_cards: [''],
    is_cui: false,
    tdp_image_preview: null
  });

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...form[field]];
    updated[index] = value;
    setForm({ ...form, [field]: updated });
  };

  const addToArray = (field) => {
    setForm({ ...form, [field]: [...form[field], ''] });
  };

  const removeFromArray = (field, index) => {
    const updated = [...form[field]];
    updated.splice(index, 1);
    setForm({ ...form, [field]: updated });
  };

  const handleFileUpload = async (index, file) => {
    const fileExtension = file.name.split('.').pop();
    const fileType = form.attached_files[index].file_type;
    const file_id = uuidv4();

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      const uploadedFile = {
        file_id,
        file_name: file.name,
        file_extension: fileExtension,
        file_type: fileType,
        storing_entity: 'amx',
        file_content_base64: base64String
      };

      const updatedFiles = [...form.attached_files];
      updatedFiles[index] = uploadedFile;
      setForm({
        ...form,
        attached_files: updatedFiles,
        tdp_image_preview:
          fileType === 'part_image'
            ? URL.createObjectURL(file)
            : form.tdp_image_preview
      });
    };
    reader.readAsDataURL(file);
  };

  const handleAddFile = () => {
    setForm({
      ...form,
      attached_files: [
        ...form.attached_files,
        {
          file_id: '',
          file_name: '',
          file_extension: '',
          file_type: '',
          storing_entity: 'amx',
          file_content_base64: ''
        }
      ]
    });
  };

  const handleFileTypeChange = (index, value) => {
    const updated = [...form.attached_files];
    updated[index].file_type = value;
    setForm({ ...form, attached_files: updated });
  };

  const handleAddPart = () => {
    setForm({
      ...form,
      parts_list: [...form.parts_list, {
        qty: 1,
        part_name: '',
        cage: '',
        part_number: '',
        material: '',
        estimated_weight_lbs: 0,
        ref_file_id: '',
        specifications: []
      }]
    });
  };

  const handlePartChange = (index, field, value) => {
    const updated = [...form.parts_list];
    updated[index][field] = value;
    setForm({ ...form, parts_list: updated });
  };

  const handleSubmit = async () => {
    if (!form.tdp_title.trim()) {
      setError('Enter a TDP title before saving.');
      return;
    }

    setSaving(true);
    setError('');
    const attachedFiles = form.attached_files
      .filter((file) => file.file_name || file.file_id)
      .map((file) => {
        const metadata = { ...file };
        delete metadata.file_content_base64;
        return metadata;
      });
    const payload = {
      name: form.tdp_title,
      version: form.version,
      status: form.status,
      amx_owning_org_id: 'navy',
      approved_by: 'user_id',
      is_cui: form.is_cui,
      tdp_title: form.tdp_title,
      designed_by: form.designed_by,
      standards: form.standards.filter(s => s),
      dimensions_units: form.dimensions_units,
      specifications: form.specifications.filter(s => s),
      attached_files: attachedFiles,
      cad_program: form.cad_program,
      parts_list: form.parts_list,
      asset_refs: form.asset_refs.filter(Boolean).map((value) => ({ label: value })),
      build_history: form.build_history.filter(Boolean).map((value) => ({ summary: value })),
      telemetry_refs: form.telemetry_refs.filter(Boolean).map((value) => ({ label: value })),
      certifications: form.certifications.filter(Boolean).map((value) => ({ label: value })),
      related_exchange_cards: form.related_exchange_cards.filter(Boolean).map((value) => ({ label: value })),
    };

    try {
      const res = await axiosInstance.post('/tdps', payload);
      const tdpId = res.data?.tdp?.tdp_id;
      if (tdpId) {
        navigate(`/tdp/${tdpId}`);
      } else {
        navigate('/x-tdp');
      }
    } catch (err) {
      console.error('Error', err);
      setError(err?.response?.data?.message || 'Unable to create this xTDP right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Box display="flex" justifyContent="space-between">
        <Box flex={1} pr={2}>
          <Typography variant="h5">New xTDP</Typography>
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
          <FormControlLabel
            control={<Switch checked={form.is_cui} onChange={(e) => handleChange('is_cui', e.target.checked)} />}
            label="CUI"
          />
          <TextField label="TDP Title" fullWidth margin="normal" value={form.tdp_title} onChange={e => handleChange('tdp_title', e.target.value)} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Version" fullWidth margin="normal" value={form.version} onChange={e => handleChange('version', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Status"
                fullWidth
                margin="normal"
                value={form.status}
                onChange={e => handleChange('status', e.target.value)}
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <TextField label="Designed By" fullWidth margin="normal" value={form.designed_by} onChange={e => handleChange('designed_by', e.target.value)} />

          <Typography variant="subtitle1">Standards</Typography>
          {form.standards.map((std, i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <TextField value={std} onChange={e => handleArrayChange('standards', i, e.target.value)} fullWidth />
              <Button onClick={() => removeFromArray('standards', i)}>Remove</Button>
            </Box>
          ))}
          <Button onClick={() => addToArray('standards')}>+ Add standard</Button>

          <TextField
            select
            label="Dimensions"
            fullWidth
            margin="normal"
            value={form.dimensions_units}
            onChange={e => handleChange('dimensions_units', e.target.value)}
          >
            {dimensionsOptions.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>

          <Typography variant="subtitle1">Specifications</Typography>
          {form.specifications.map((spec, i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <TextField value={spec} onChange={e => handleArrayChange('specifications', i, e.target.value)} fullWidth />
              <Button onClick={() => removeFromArray('specifications', i)}>Remove</Button>
            </Box>
          ))}
          <Button onClick={() => addToArray('specifications')}>+ Add specification</Button>

          <TextField label="CAD Program" fullWidth margin="normal" value={form.cad_program} onChange={e => handleChange('cad_program', e.target.value)} />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              ['asset_refs', 'Referenced Assets'],
              ['build_history', 'Build History'],
              ['telemetry_refs', 'Telemetry References'],
              ['certifications', 'Certifications'],
              ['related_exchange_cards', 'Related Exchange Cards'],
            ].map(([field, label]) => (
              <Grid item xs={12} md={6} key={field}>
                <Typography variant="subtitle1">{label}</Typography>
                <Stack spacing={1}>
                  {form[field].map((value, i) => (
                    <Box key={`${field}-${i}`} display="flex" gap={1} alignItems="center">
                      <TextField value={value} onChange={e => handleArrayChange(field, i, e.target.value)} fullWidth />
                      <Button onClick={() => removeFromArray(field, i)}>Remove</Button>
                    </Box>
                  ))}
                </Stack>
                <Button onClick={() => addToArray(field)}>+ Add</Button>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box width={300} textAlign="center" border={1} borderColor="grey.400" borderRadius={2} p={1}>
          <Typography variant="subtitle1">TDP Image</Typography>
          {form.tdp_image_preview ? (
            <img src={form.tdp_image_preview} alt="Part Preview" style={{ maxWidth: '100%', maxHeight: 200 }} />
          ) : (
            <Typography variant="body2" color="textSecondary">Image will appear here</Typography>
          )}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mt: 2 }}>Attach Files</Typography>
      {form.attached_files.map((file, idx) => (
        <Grid container spacing={2} key={idx} alignItems="center">
          <Grid item xs={2}>
            <TextField
              select
              label="Type"
              value={file.file_type}
              onChange={e => handleFileTypeChange(idx, e.target.value)}
              fullWidth
            >
              {fileTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <input
              type="file"
              onChange={e => handleFileUpload(idx, e.target.files[0])}
            />
          </Grid>
        </Grid>
      ))}
      <Button onClick={handleAddFile}>+ Add file</Button>

      <Typography variant="h6" sx={{ mt: 2 }}>Parts List</Typography>
      <Button onClick={handleAddPart}>+ Add Part</Button>

      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Qty</TableCell>
            <TableCell>Part Name</TableCell>
            <TableCell>CAGE</TableCell>
            <TableCell>Part #</TableCell>
            <TableCell>Material</TableCell>
            <TableCell>Weight (lbs)</TableCell>
            <TableCell>Ref File</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {form.parts_list.map((part, i) => (
            <TableRow key={i}>
              <TableCell><TextField value={part.qty} onChange={e => handlePartChange(i, 'qty', e.target.value)} fullWidth /></TableCell>
              <TableCell><TextField value={part.part_name} onChange={e => handlePartChange(i, 'part_name', e.target.value)} fullWidth /></TableCell>
              <TableCell><TextField value={part.cage} onChange={e => handlePartChange(i, 'cage', e.target.value)} fullWidth /></TableCell>
              <TableCell><TextField value={part.part_number} onChange={e => handlePartChange(i, 'part_number', e.target.value)} fullWidth /></TableCell>
              <TableCell><TextField value={part.material} onChange={e => handlePartChange(i, 'material', e.target.value)} fullWidth /></TableCell>
              <TableCell><TextField value={part.estimated_weight_lbs} onChange={e => handlePartChange(i, 'estimated_weight_lbs', e.target.value)} fullWidth /></TableCell>
              <TableCell>
                <FormControl fullWidth>
                  <Select
                    value={part.ref_file_id}
                    onChange={e => handlePartChange(i, 'ref_file_id', e.target.value)}
                  >
                    {form.attached_files.map(file => (
                      <MenuItem key={file.file_id} value={file.file_id}>{file.file_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="outlined" onClick={() => navigate('/x-tdp')} disabled={saving}>Cancel</Button>
      </Box>
    </Paper>
  );
};

export default NewTDPForm;
